import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Building } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import ReportViewer from './reports/ReportViewer';

const Reports = () => {
  const [activeReport, setActiveReport] = useState('trial-balance');
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branchId: '',
    from: '',
    to: '',
    period: 'monthly',
    search: '',
    status: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pages: 1,
    total: 0,
    limit: 25
  });
  const [pageSize, setPageSize] = useState(25);

  const reportTypes = [
    { id: 'trial-balance', name: 'Trial Balance', icon: FileText },
    { id: 'balance-sheet', name: 'Balance Sheet', icon: FileText },
    { id: 'profit-loss', name: 'P&L Report', icon: FileText },
    { id: 'cash-accounts', name: 'Cash Accounts', icon: FileText },
    { id: 'journal', name: 'Journal', icon: FileText },
    { id: 'vat', name: 'VAT Report', icon: FileText },
    { id: 'customer-receivable', name: 'Customer Receivable', icon: FileText },
    { id: 'sales-orders', name: 'Sales Order', icon: FileText },
    { id: 'sales-return', name: 'Sales Return', icon: FileText },
    { id: 'job-margin', name: 'Job Margin', icon: FileText },
    { id: 'vendor-payable', name: 'Vendor Payable', icon: FileText },
    { id: 'purchase', name: 'Purchase Report', icon: FileText },
    { id: 'inventory', name: 'Inventory Report', icon: FileText },
    { id: 'order-report', name: 'Order Report', icon: FileText },
    { id: 'all-orders', name: 'All Orders Report', icon: FileText }
  ];

  useEffect(() => {
    fetchBranches();
    // Check for URL parameter to set active report
    const urlParams = new URLSearchParams(window.location.search);
    const reportType = urlParams.get('type');
    if (reportType && reportTypes.find(r => r.id === reportType)) {
      setActiveReport(reportType);
    }
  }, []);

  useEffect(() => {
    if (activeReport) {
      fetchReport(1);
    }
  }, [activeReport, pageSize]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      setBranches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const fetchReport = async (page = 1) => {
    if (!activeReport) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && key !== 'search') params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/reports/${activeReport}?${params}`);
      let data = response.data;
      
      // Handle object-based reports (balance-sheet, profit-loss)
      if (['balance-sheet', 'profit-loss'].includes(activeReport)) {
        setReportData(data);
        setPagination({ currentPage: 1, pages: 1, total: 1, limit: pageSize });
        setLoading(false);
        return;
      }
      
      // Handle complex reports with nested structure
      if (['order-report', 'all-orders'].includes(activeReport)) {
        setReportData(data);
        setPagination({ currentPage: 1, pages: 1, total: 1, limit: pageSize });
        setLoading(false);
        return;
      }
      
      // Handle array-based reports
      if (!Array.isArray(data)) {
        data = [];
      }
      
      // Client-side filtering
      let filteredData = data;
      if (filters.search && Array.isArray(data)) {
        const searchLower = filters.search.toLowerCase();
        filteredData = data.filter(item => 
          Object.values(item).some(value => 
            String(value).toLowerCase().includes(searchLower)
          )
        );
      }
      
      if (filters.status && Array.isArray(filteredData)) {
        filteredData = filteredData.filter(item => 
          item.status === filters.status
        );
      }
      
      // Client-side pagination
      const total = filteredData.length;
      const pages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = pageSize === 500 ? filteredData : filteredData.slice(startIndex, endIndex);
      
      setReportData(paginatedData);
      setPagination({
        currentPage: page,
        pages: pageSize === 500 ? 1 : pages,
        total,
        limit: pageSize
      });
    } catch (error) {
      toast.error('Failed to fetch report data');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Debounced search effect
  useEffect(() => {
    if (!activeReport) return;
    
    const delayedSearch = setTimeout(() => {
      fetchReport(1);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [filters.search, filters.status, filters.from, filters.to, filters.branchId]);

  const handleExport = () => {
    if (!reportData) {
      toast.error('No data to export');
      return;
    }
    
    // Convert data to CSV
    const csvContent = convertToCSV(reportData);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeReport}-report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  const convertToCSV = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return '';
    
    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') return '';
    
    const headers = Object.keys(firstRow).join(',');
    const rows = data.map(row => 
      Object.values(row || {}).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Sidebar - horizontal scroll on mobile, vertical on desktop */}
      <div className="md:w-64 bg-white shadow-sm border-b md:border-b-0 md:border-r flex-shrink-0">
        <div className="p-4 md:p-6">
          <h1 className="text-lg md:text-xl font-semibold text-gray-900">Reports</h1>
        </div>
        <nav className="px-3 pb-3 md:pb-0 flex md:block overflow-x-auto md:overflow-x-visible gap-1 md:gap-0">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            return (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id)}
                className={`flex items-center px-3 py-2 mb-0 md:mb-1 text-left rounded-md transition-colors whitespace-nowrap text-sm ${
                  activeReport === report.id
                    ? 'bg-blue-50 text-blue-700 border-b-2 md:border-b-0 md:border-r-2 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 mr-2 md:mr-3 flex-shrink-0" />
                {report.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {reportTypes.find(r => r.id === activeReport)?.name}
            </h2>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Status Filter */}
                {(activeReport === 'sales-return' || activeReport === 'sales-orders') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>
                )}

                {/* Branch Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Building className="w-4 h-4 inline mr-1" />
                    Branch
                  </label>
                  <select
                    value={filters.branchId}
                    onChange={(e) => handleFilterChange('branchId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Branches</option>
                    {Array.isArray(branches) && branches.map(branch => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>

                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    From
                  </label>
                  <input
                    type="date"
                    value={filters.from}
                    onChange={(e) => handleFilterChange('from', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <input
                    type="date"
                    value={filters.to}
                    onChange={(e) => handleFilterChange('to', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Period (for P&L) */}
                {activeReport === 'profit-loss' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                    <select
                      value={filters.period}
                      onChange={(e) => handleFilterChange('period', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = parseInt(e.target.value);
                      setPageSize(newSize);
                      fetchReport(1);
                    }}
                    className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                    <option value={500}>Show All</option>
                  </select>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchReport(1)}
                    disabled={loading}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Submit'}
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={!reportData || loading}
                    className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Report Content */}
          <div className="bg-white rounded-lg shadow border">
            <ReportViewer
              type={activeReport}
              data={reportData}
              loading={loading}
            />
            
            {/* Pagination */}
            {Array.isArray(reportData) && pagination.total > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-white">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-semibold">{((pagination.currentPage - 1) * pagination.limit) + 1}</span> to{' '}
                    <span className="font-semibold">{Math.min(pagination.currentPage * pagination.limit, pagination.total)}</span> of{' '}
                    <span className="font-semibold">{pagination.total}</span> results
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => fetchReport(1)}
                      disabled={pagination.currentPage === 1 || loading}
                      className="px-2 py-2 border rounded-md disabled:opacity-50 hover:bg-gray-100 text-sm"
                    >
                      ««
                    </button>
                    <button
                      onClick={() => fetchReport(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1 || loading}
                      className="px-3 py-2 border rounded-md disabled:opacity-50 hover:bg-gray-100 text-sm"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      let pageNum = i + 1;
                      if (pagination.pages > 5) {
                        if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.pages - 2) {
                          pageNum = pagination.pages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => fetchReport(pageNum)}
                          disabled={loading}
                          className={`px-3 py-2 border rounded-md text-sm ${
                            pageNum === pagination.currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => fetchReport(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.pages || loading}
                      className="px-3 py-2 border rounded-md disabled:opacity-50 hover:bg-gray-100 text-sm"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => fetchReport(pagination.pages)}
                      disabled={pagination.currentPage === pagination.pages || loading}
                      className="px-2 py-2 border rounded-md disabled:opacity-50 hover:bg-gray-100 text-sm"
                    >
                      »»
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;